// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Image, Modal, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Location from 'expo-location';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '@/styles/datepicker.css';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAppLogger } from '@/services/appLogger';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';

import BestBallSelectImage from '@/assets/images/games/best-ball-select.png';
import ThreePuttsSelectImage from '@/assets/images/games/3-putts-select.png';
import BunkerSelectImage from '@/assets/images/games/bunker-select.png';
import HighLowSelectImage from '@/assets/images/games/high-low-select.png';
import StablefordSelectImage from '@/assets/images/games/stableford-select.png';
import SkinsSelectImage from '@/assets/images/games/skins-select.png';
import MysterySelectImage from '@/assets/images/games/mystery-select.png';
import PenaltySelectImage from '@/assets/images/games/penalty-select.png';
import WaterSelectImage from '@/assets/images/games/water-select.png';
import WorstBallSelectImage from '@/assets/images/games/worst-ball-select.png';
import type { Course, TourRoundSummary } from '@/features/round-init/types';
import { getGameDisplayDescription, getGameDisplayInfo, getGameDisplayName } from '@/features/game-definitions/i18n';
type GameDefinition = {
  id: string;
  name: string;
  info?: string | null;
  description?: string | null;
  competitionType?: string | null;
  calculation?: string | null;
  gameType?: string | null;
  status?: string | null;
  defaultWeightPercent?: number | null;
};
type TourPlayerSnapshot = { id: string; name: string; userId?: string | null };

const GAME_IMAGE_MAP: Record<string, number> = {
  'best-ball': BestBallSelectImage,
  '3-putts': ThreePuttsSelectImage,
  bunker: BunkerSelectImage,
  'high-low': HighLowSelectImage,
  mystery: MysterySelectImage,
  penalty: PenaltySelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  water: WaterSelectImage,
  'worst-ball': WorstBallSelectImage,
};
const WEIGHT_STEPS = [25, 50, 75, 100];

export default function TourEditWebScreen() {
  const { tourId, debug: debugParam } = useLocalSearchParams<{ tourId?: string; debug?: string }>();
  const debug = debugParam === 'true';
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'tour-edit',
      }),
    [apiBaseUrl, user?.token]
  );
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const [tourName, setTourName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState<string>('');
  const [tourImageKey, setTourImageKey] = useState<string | null>(null);
  const [tourImageVersion, setTourImageVersion] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const imageRequestIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingRequestIdRef = useRef<string | null>(null);
  const lastPlayersSignatureRef = useRef<string>('');
  const lastTourNameRef = useRef<string>('');
  const pendingPayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingRoundUpdatesRef = useRef<
    Record<
      string,
      {
        type: 'round.update';
        roundId: string;
        gameDefinitionIds: string[];
        gameWeights: Array<{ gameDefinitionId: string; weightPercent: number }>;
        playDate?: string;
      }
    >
  >({});
  const ackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rounds, setRounds] = useState<TourRoundSummary[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [roundGamesByRoundId, setRoundGamesByRoundId] = useState<
    Record<string, { loading: boolean; error: string | null; gameIds: string[]; weightsByGameId: Record<string, number> }>
  >({});
  const [expandedGameId, setExpandedGameId] = useState<string | null>(null);
  const [hoveredGameKey, setHoveredGameKey] = useState<string | null>(null);
  const [hoveredWeightKey, setHoveredWeightKey] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [addRoundId, setAddRoundId] = useState<string | null>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addRoundOpen, setAddRoundOpen] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [roundNameInput, setRoundNameInput] = useState('');
  const [roundDescription, setRoundDescription] = useState('');
  const [roundDescriptionOpen, setRoundDescriptionOpen] = useState(false);
  const [holesCount, setHolesCount] = useState(18);
  const [roundPlayDateInput, setRoundPlayDateInput] = useState(() => new Date().toISOString().slice(0, 10));
  const [roundPlayDateTargetId, setRoundPlayDateTargetId] = useState<string | null>(null);
  const [roundPlayDateValue, setRoundPlayDateValue] = useState<Date | null>(null);
  const [playDatePickerOpen, setPlayDatePickerOpen] = useState(false);
  const [creatingRound, setCreatingRound] = useState(false);
  const [createRoundError, setCreateRoundError] = useState<string | null>(null);
  const [tourPlayers, setTourPlayers] = useState<Array<{ id: string; scp?: string }>>([]);
  const [tourPlayersLoading, setTourPlayersLoading] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState<TourPlayerSnapshot[]>([]);
  const [playerRows, setPlayerRows] = useState<Array<{ id?: string; name?: string; query: string; locked?: boolean }>>([]);
  const [playerRowOpen, setPlayerRowOpen] = useState<number | null>(null);
  const playersInitializedRef = useRef(false);
  const addRoundRequestIdRef = useRef<string | null>(null);
  const addRoundAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStateRef = useRef<{ gameId: string; sourceRoundId: string | null } | null>(null);
  const dropTargetRef = useRef<{ roundId?: string } | null>(null);
  const pendingPlayDateRef = useRef<Record<string, Date>>({});

  const getTodayDateInput = () => new Date().toISOString().slice(0, 10);
  const parseDateInput = (value: string) => {
    if (!value) return null;
    const parts = value.split('-').map((item) => Number.parseInt(item, 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };
  const formatDateInput = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const selectedPlayDate = parseDateInput(roundPlayDateInput) ?? new Date();
  const selectedRoundPlayDate = roundPlayDateValue ?? new Date();
  const getDefaultWeightForGame = useCallback(
    (gameId: string) => {
      const match = gameDefinitions.find((game) => game.id === gameId);
      const fallback = match?.defaultWeightPercent;
      return typeof fallback === 'number' && !Number.isNaN(fallback) ? fallback : 100;
    },
    [gameDefinitions]
  );
  const buildWeightsMap = useCallback(
    (gameIds: string[], existing: Record<string, number> | undefined) => {
      const next: Record<string, number> = {};
      gameIds.forEach((id) => {
        if (!id) return;
        const weight = existing?.[id];
        next[id] = typeof weight === 'number' && !Number.isNaN(weight) ? weight : getDefaultWeightForGame(id);
      });
      return next;
    },
    [getDefaultWeightForGame]
  );
  const formatGameType = useCallback((value?: string | null) => {
    if (!value) return '';
    return value.replace(/\bFormat\b/gi, '').replace(/\s+/g, ' ').trim();
  }, []);

  const fetchTour = useCallback(async () => {
    if (!tourId || !user?.token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load tour (${response.status})`);
      }
      const data = (await response.json()) as {
        tourName?: string;
        TourName?: string;
        name?: string;
        Name?: string;
        imageKey?: string | null;
        ImageKey?: string | null;
      };
      const incomingName = data.tourName ?? data.TourName ?? data.name ?? data.Name ?? '';
      setTourName(incomingName);
      lastTourNameRef.current = incomingName.trim();
      const incomingImageKey = data.imageKey ?? data.ImageKey ?? null;
      setTourImageKey(incomingImageKey);
      if (incomingImageKey) {
        setTourImageVersion((prev) => prev + 1);
      }
    } catch (err) {
      setError(t('tourEdit.loadError'));
      logToBff('tourEdit.load.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, logToBff, t, tourId, user?.token]);

  const fetchRounds = useCallback(async () => {
    if (!tourId || !user?.token) return;
    setRoundsLoading(true);
    setRoundsError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,active,playing,finished`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load rounds (${response.status})`);
      }
      const data = (await response.json()) as TourRoundSummary[];
      setRounds(data ?? []);
    } catch (err) {
      setRounds([]);
      setRoundsError(t('tournaments.roundsLoadError'));
      logToBff('tourEdit.rounds.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setRoundsLoading(false);
    }
  }, [apiBaseUrl, logToBff, t, tourId, user?.token]);

  const fetchGameDefinitions = useCallback(async () => {
    if (!user?.token) return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to load game definitions (${response.status})`);
      }
      const data = (await response.json()) as Array<Record<string, unknown>>;
      const normalized = (data ?? []).map((entry) => ({
        id: ((entry['id'] as string | undefined) ?? (entry['Id'] as string | undefined) ?? '').trim(),
        name: ((entry['name'] as string | undefined) ?? (entry['Name'] as string | undefined) ?? '').trim(),
        info: (entry['info'] as string | null | undefined) ?? (entry['Info'] as string | null | undefined) ?? null,
        description:
          (entry['description'] as string | null | undefined) ??
          (entry['Description'] as string | null | undefined) ??
          null,
        competitionType:
          (entry['competitionType'] as string | null | undefined) ??
          (entry['CompetitionType'] as string | null | undefined) ??
          null,
        calculation:
          (entry['calculation'] as string | null | undefined) ??
          (entry['Calculation'] as string | null | undefined) ??
          null,
        gameType:
          (entry['gameType'] as string | null | undefined) ?? (entry['GameType'] as string | null | undefined) ?? null,
        status: (entry['status'] as string | null | undefined) ?? (entry['Status'] as string | null | undefined) ?? null,
        defaultWeightPercent:
          (entry['defaultWeightPercent'] as number | undefined) ??
          (entry['DefaultWeightPercent'] as number | undefined) ??
          100,
      }));
      setGameDefinitions(normalized.filter((entry) => entry.id.length > 0 && entry.name.length > 0));
    } catch {
      setGameDefinitions([]);
    }
  }, [apiBaseUrl, user?.token]);

  const fetchRoundGames = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: {
          loading: true,
          error: null,
          gameIds: prev[roundId]?.gameIds ?? [],
          weightsByGameId: prev[roundId]?.weightsByGameId ?? {},
        },
      }));
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load round (${response.status})`);
        }
        const data = (await response.json()) as {
          gameDefinitions?: string[];
          GameDefinitions?: string[];
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
          gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
          GameWeights?: Array<{ GameDefinitionId?: string; WeightPercent?: number }>;
        };
        const unique = Array.from(
          new Set(
            [
              ...(data.gameDefinitions ?? []),
              ...(data.GameDefinitions ?? []),
              ...(data.gameDefinitionIds ?? []),
              ...(data.GameDefinitionIds ?? []),
            ].filter(Boolean)
          )
        );
        const weightsByGameId: Record<string, number> = {};
        const incomingWeights = [
          ...(data.gameWeights ?? []),
          ...(data.GameWeights ?? []),
        ] as Array<Record<string, unknown>>;
        incomingWeights.forEach((entry) => {
          const id =
            (entry.gameDefinitionId as string | undefined) ??
            (entry.GameDefinitionId as string | undefined);
          if (!id) return;
          const weight =
            (entry.weightPercent as number | undefined) ??
            (entry.WeightPercent as number | undefined);
          if (typeof weight === 'number' && !Number.isNaN(weight)) {
            weightsByGameId[id] = weight;
          }
        });
        const normalizedWeights = buildWeightsMap(unique, weightsByGameId);
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: null,
            gameIds: unique,
            weightsByGameId: normalizedWeights,
          },
        }));
        const pending = pendingPlayDateRef.current[roundId];
        if (pending) {
          delete pendingPlayDateRef.current[roundId];
          updateRoundPlayDate(roundId, pending);
        }
      } catch (err) {
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: {
            loading: false,
            error: t('tournaments.gamesLoadError'),
            gameIds: [],
            weightsByGameId: {},
          },
        }));
      }
    },
    [apiBaseUrl, buildWeightsMap, t, user?.token]
  );

  const updateRoundGames = useCallback(
    (roundId: string, gameIds: string[], weightsOverride?: Record<string, number>, playDate?: Date | null) => {
      if (!roundId || !user?.token) return;
      const unique = Array.from(new Set(gameIds.filter(Boolean)));
      const weightsByGameId = buildWeightsMap(unique, weightsOverride ?? roundGamesByRoundId[roundId]?.weightsByGameId);
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: {
          loading: false,
          error: null,
          gameIds: unique,
          weightsByGameId,
        },
      }));
      const socket = socketRef.current;
      socket.setAuthToken(user?.token ?? null);
      const payload = {
        type: 'round.update',
        roundId,
        gameDefinitionIds: unique,
        gameWeights: unique.map((id) => ({
          gameDefinitionId: id,
          weightPercent: weightsByGameId[id] ?? getDefaultWeightForGame(id),
        })),
        ...(playDate ? { playDate: playDate.toISOString() } : {}),
      } as const;
      const sent = socket.send(payload);
      if (!sent) {
        pendingRoundUpdatesRef.current[roundId] = {
          ...payload,
          roundId,
          gameDefinitionIds: [...payload.gameDefinitionIds],
          gameWeights: payload.gameWeights.map((item) => ({ ...item })),
          ...(payload.playDate ? { playDate: payload.playDate } : {}),
        };
      } else {
        delete pendingRoundUpdatesRef.current[roundId];
      }
    },
    [buildWeightsMap, getDefaultWeightForGame, roundGamesByRoundId, user?.token]
  );

  const updateRoundPlayDate = useCallback(
    (roundId: string, date: Date) => {
      const roundState = roundGamesByRoundId[roundId];
      if (!roundState || roundState.loading) {
        pendingPlayDateRef.current[roundId] = date;
        fetchRoundGames(roundId);
        return;
      }
      updateRoundGames(roundId, roundState.gameIds ?? [], roundState.weightsByGameId, date);
      setRounds((prev) =>
        prev.map((round) => (round.id === roundId ? { ...round, playDate: date.toISOString() } : round))
      );
    },
    [fetchRoundGames, roundGamesByRoundId, updateRoundGames]
  );

  const handleDropToRound = useCallback(
    (roundId: string, gameIdOverride?: string | null) => {
      const dragState = dragStateRef.current;
      const gameId = gameIdOverride ?? dragState?.gameId ?? null;
      if (!gameId) return;
      if (dragState?.sourceRoundId === roundId) return;
      const targetGames = roundGamesByRoundId[roundId]?.gameIds ?? [];
      if (!targetGames.includes(gameId)) {
        updateRoundGames(roundId, [...targetGames, gameId]);
      }
      if (dragState?.sourceRoundId) {
        const sourceGames = roundGamesByRoundId[dragState.sourceRoundId]?.gameIds ?? [];
        if (sourceGames.includes(gameId)) {
          updateRoundGames(dragState.sourceRoundId, sourceGames.filter((id) => id !== gameId));
        }
      }
    },
    [roundGamesByRoundId, updateRoundGames]
  );

  const handleDragEnd = useCallback(() => {
    const dragState = dragStateRef.current;
    const dropTarget = dropTargetRef.current;
    if (dragState?.sourceRoundId && !dropTarget?.roundId) {
      const sourceGames = roundGamesByRoundId[dragState.sourceRoundId]?.gameIds ?? [];
      if (sourceGames.includes(dragState.gameId)) {
        updateRoundGames(dragState.sourceRoundId, sourceGames.filter((id) => id !== dragState.gameId));
      }
    }
    dragStateRef.current = null;
    dropTargetRef.current = null;
    setIsDragging(false);
  }, [roundGamesByRoundId, updateRoundGames]);

  const handlePointerDrop = useCallback(
    (roundId: string) => {
      if (!isDragging) return;
      const gameId = dragStateRef.current?.gameId ?? null;
      if (!gameId) return;
      handleDropToRound(roundId, gameId);
      setIsDragging(false);
      dragStateRef.current = null;
      dropTargetRef.current = null;
    },
    [handleDropToRound, isDragging]
  );

  const handlePointerDragStart = useCallback((gameId: string, sourceRoundId: string | null) => {
    dragStateRef.current = { gameId, sourceRoundId };
    dropTargetRef.current = null;
    setIsDragging(true);
  }, []);

  const handlePointerDragEnd = useCallback(() => {
    const dragState = dragStateRef.current;
    const dropTarget = dropTargetRef.current;
    if (dragState && dropTarget?.roundId) {
      handleDropToRound(dropTarget.roundId, dragState.gameId);
      setIsDragging(false);
      dragStateRef.current = null;
      dropTargetRef.current = null;
      return;
    }
    if (dragState?.sourceRoundId) {
      const sourceGames = roundGamesByRoundId[dragState.sourceRoundId]?.gameIds ?? [];
      if (sourceGames.includes(dragState.gameId)) {
        updateRoundGames(dragState.sourceRoundId, sourceGames.filter((id) => id !== dragState.gameId));
      }
    }
    setIsDragging(false);
    dragStateRef.current = null;
    dropTargetRef.current = null;
  }, [handleDropToRound, roundGamesByRoundId, updateRoundGames]);

  const openAddMenu = useCallback((roundId: string) => {
    setAddRoundId(roundId);
    setAddMenuOpen(true);
  }, []);

  const closeAddMenu = useCallback(() => {
    setAddMenuOpen(false);
    setAddRoundId(null);
  }, []);

  const handleAddGame = useCallback(
    (roundId: string, gameId: string) => {
      const current = roundGamesByRoundId[roundId]?.gameIds ?? [];
      if (current.includes(gameId)) return;
      updateRoundGames(roundId, [...current, gameId]);
      closeAddMenu();
    },
    [closeAddMenu, roundGamesByRoundId, updateRoundGames]
  );

  useEffect(() => {
    fetchTour();
  }, [fetchTour]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  useEffect(() => {
    fetchGameDefinitions();
  }, [fetchGameDefinitions]);

  useEffect(() => {
    rounds.forEach((round) => {
      if (!roundGamesByRoundId[round.id]) {
        fetchRoundGames(round.id);
      }
    });
  }, [fetchRoundGames, roundGamesByRoundId, rounds]);

  useEffect(() => {
    return () => {
      if (ackTimeoutRef.current) {
        clearTimeout(ackTimeoutRef.current);
        ackTimeoutRef.current = null;
      }
      if (addRoundAckTimeoutRef.current) {
        clearTimeout(addRoundAckTimeoutRef.current);
        addRoundAckTimeoutRef.current = null;
      }
    };
  }, []);

  const fetchTourPlayers = useCallback(async () => {
    if (!user?.token) return;
    setTourPlayersLoading(true);
    try {
      const playersResp = await fetch(`${apiBaseUrl}/api/players`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!playersResp.ok) {
        throw new Error(`Failed to load players (${playersResp.status})`);
      }
      const data = (await playersResp.json()) as { players?: TourPlayerSnapshot[]; Players?: TourPlayerSnapshot[] };
      const players = (data.players ?? data.Players ?? []).map((player) => ({
        id: player.id,
        name: player.name,
        userId: (player as { userId?: string | null }).userId ?? (player as { UserId?: string | null }).UserId ?? null,
      }));
      setAvailablePlayers(players);

      let currentPlayerId: string | null = null;
      if (user?.id) {
        const current = players.find((p) => p.userId === user.id);
        currentPlayerId = current?.id ?? null;
      }
      if (!currentPlayerId && user?.id) {
        const playerResp = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (playerResp.ok) {
          const player = (await playerResp.json()) as { id?: string };
          currentPlayerId = player?.id ?? null;
        }
      }

      if (!playersInitializedRef.current) {
        const currentPlayer = currentPlayerId ? players.find((p) => p.id === currentPlayerId) : null;
        const currentName = currentPlayer?.name ?? t('tourEdit.playerCurrentUserFallback');
        const locked = Boolean(currentPlayerId);
        const rows = [
          { id: currentPlayerId ?? undefined, name: currentName, query: currentName, locked },
          { id: undefined, name: undefined, query: '', locked: false },
        ];
        setPlayerRows(rows);
        const signature = rows
          .filter((row) => row.id)
          .map((row) => row.id as string)
          .sort()
          .join('|');
        lastPlayersSignatureRef.current = signature;
        playersInitializedRef.current = true;
      }
    } catch {
      setAvailablePlayers([]);
      if (!playersInitializedRef.current) {
        const rows = [{ id: undefined, name: undefined, query: '', locked: false }];
        setPlayerRows(rows);
        lastPlayersSignatureRef.current = '';
        playersInitializedRef.current = true;
      }
    } finally {
      setTourPlayersLoading(false);
    }
  }, [apiBaseUrl, t, user?.id, user?.token]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'tour.update.ack') {
        const payload = message as { requestId?: string; tourId?: string };
        if (pendingRequestIdRef.current && payload.requestId !== pendingRequestIdRef.current) {
          fetchTour();
          fetchTourPlayers();
          fetchRounds();
          return;
        }
        pendingRequestIdRef.current = null;
        if (ackTimeoutRef.current) {
          clearTimeout(ackTimeoutRef.current);
          ackTimeoutRef.current = null;
        }
        setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        logToBff('tourEdit.update.ack', { tourId: payload.tourId ?? tourId, requestId: payload.requestId });
        return;
      }
      if (type === 'round.updated') {
        const payload = message as {
          roundId?: string;
          gameDefinitionIds?: string[];
          gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
          playDate?: string;
        };
        if (!payload.roundId) return;
        if (payload.playDate) {
          setRounds((prev) =>
            prev.map((round) =>
              round.id === payload.roundId ? { ...round, playDate: payload.playDate } : round
            )
          );
        }
        if (payload.gameDefinitionIds) {
          setRoundGamesByRoundId((prev) => {
            const existing = prev[payload.roundId as string];
            const gameIds = payload.gameDefinitionIds ?? [];
            const mergedWeights = buildWeightsMap(gameIds, existing?.weightsByGameId);
            (payload.gameWeights ?? []).forEach((entry) => {
              if (entry.gameDefinitionId && typeof entry.weightPercent === 'number' && !Number.isNaN(entry.weightPercent)) {
                mergedWeights[entry.gameDefinitionId] = entry.weightPercent;
              }
            });
            return {
              ...prev,
              [payload.roundId as string]: {
                loading: false,
                error: null,
                gameIds,
                weightsByGameId: buildWeightsMap(gameIds, mergedWeights),
              },
            };
          });
          return;
        }
        fetchRoundGames(payload.roundId);
        return;
      }
      if (type === 'tour.image.presign.ack') {
        const payload = message as { requestId?: string; uploadUrl?: string; publicUrl?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        const file = pendingImageFileRef.current;
        if (!file || !payload.uploadUrl || !payload.publicUrl) {
          imageRequestIdRef.current = null;
          setImageUploading(false);
          setImageError(t('tourEdit.imageUploadFailed'));
          return;
        }
        (async () => {
          try {
            const uploadResp = await fetch(payload.uploadUrl!, {
              method: 'PUT',
              headers: { 'Content-Type': file.type || 'image/jpeg' },
              body: file,
            });
            if (!uploadResp.ok) {
              throw new Error(`Upload failed ${uploadResp.status}`);
            }
            const updateRequestId = generateId();
            pendingRequestIdRef.current = updateRequestId;
            socketRef.current.setAuthToken(user?.token ?? null);
            socketRef.current.send({
              type: 'tour.update',
              tourId,
              requestId: updateRequestId,
              imageKey: payload.publicUrl,
            });
            setTourImageKey(payload.publicUrl ?? null);
            setTourImageVersion((prev) => prev + 1);
            setImageUploading(false);
            setImageError(null);
            setStatusNote(t('tourEdit.imageUploadSuccess'));
          } catch {
            setImageUploading(false);
            setImageError(t('tourEdit.imageUploadFailed'));
          } finally {
            imageRequestIdRef.current = null;
            pendingImageFileRef.current = null;
          }
        })();
        return;
      }
      if (type === 'tour.image.presign.error') {
        const payload = message as { requestId?: string; message?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        imageRequestIdRef.current = null;
        pendingImageFileRef.current = null;
        setImageUploading(false);
        setImageError(payload.message ?? t('tourEdit.imageUploadFailed'));
        return;
      }
      if (type === 'round.created' || type === 'round.initiate.ack') {
        const payload = message as { requestId?: string; roundId?: string; tourId?: string };
        if (addRoundRequestIdRef.current && payload.requestId && payload.requestId !== addRoundRequestIdRef.current) {
          return;
        }
        addRoundRequestIdRef.current = null;
        if (addRoundAckTimeoutRef.current) {
          clearTimeout(addRoundAckTimeoutRef.current);
          addRoundAckTimeoutRef.current = null;
        }
        setCreatingRound(false);
        setAddRoundOpen(false);
        setRoundNameInput('');
        setRoundDescription('');
        setSelectedCourseId(null);
        setCreateRoundError(null);
        fetchRounds();
      }
    });
    const unsubscribeError = socket.onError(() => {
      setError(t('tourEdit.saveFailed'));
      setSaving(false);
    });
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state !== 'connected') return;
      if (pendingPayloadRef.current) {
        const payload = pendingPayloadRef.current;
        pendingPayloadRef.current = null;
        const sent = socket.send(payload);
        if (sent) {
          setStatusNote(t('tourEdit.saveQueued'));
        } else {
          pendingPayloadRef.current = payload;
        }
      }
      const pendingRoundIds = Object.keys(pendingRoundUpdatesRef.current);
      pendingRoundIds.forEach((roundId) => {
        const payload = pendingRoundUpdatesRef.current[roundId];
        const sent = socket.send(payload);
        if (sent) {
          delete pendingRoundUpdatesRef.current[roundId];
        }
      });
      if (Object.keys(pendingRoundUpdatesRef.current).length > 0) {
        const roundId = Object.keys(pendingRoundUpdatesRef.current)[0];
        const payload = pendingRoundUpdatesRef.current[roundId];
        if (payload) {
          fetchRoundGames(payload.roundId);
        }
      }
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      unsubscribeConnection();
    };
    }, [buildWeightsMap, fetchRoundGames, fetchRounds, fetchTour, fetchTourPlayers, logToBff, t, tourId, user?.token]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) ?? null,
    [courses, selectedCourseId]
  );

  const fetchCourses = useCallback(async () => {
    if (!user?.token) return;
    setLoadingCourses(true);
    setCourseError(null);
    try {
      if (!coords) {
        setCourseError(t('course.error'));
        setLoadingCourses(false);
        return;
      }
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses?lat=${coords.lat}&lon=${coords.lon}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) throw new Error(`Failed to load courses (${response.status})`);
      const data = (await response.json()) as Course[];
      setCourses(data ?? []);
      setSelectedCourseId((prev) => {
        if (prev && data.some((course) => course.id === prev)) return prev;
        const chalmers = data.find((course) => course.name.toLowerCase().includes('chalmers'));
        return chalmers?.id ?? data[0]?.id ?? null;
      });
    } catch {
      setCourses([]);
      setCourseError(t('course.error'));
    } finally {
      setLoadingCourses(false);
    }
  }, [apiBaseUrl, coords, t, user?.token]);

  useEffect(() => {
    if (!addRoundOpen || coords) return;
    const requestLocation = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setLocationDenied(true);
          setCoords({ lat: 57.7089, lon: 11.9746 });
          return;
        }
        const position = await Location.getCurrentPositionAsync({});
        setCoords({ lat: position.coords.latitude, lon: position.coords.longitude });
      } catch {
        setLocationDenied(true);
        setCoords({ lat: 57.7089, lon: 11.9746 });
      }
    };
    requestLocation().catch(() => {
      setLocationDenied(true);
      setCoords({ lat: 57.7089, lon: 11.9746 });
    });
  }, [addRoundOpen, coords]);

  useEffect(() => {
    if (!addRoundOpen || !coords) return;
    fetchCourses();
  }, [addRoundOpen, coords, fetchCourses]);

  useEffect(() => {
    fetchTourPlayers();
  }, [fetchTourPlayers]);

  useEffect(() => {
    if (!addRoundOpen) return;
    setRoundPlayDateInput(getTodayDateInput());
  }, [addRoundOpen]);

  useEffect(() => {
    const selected = playerRows
      .filter((row) => row.id)
      .map((row) => ({ id: row.id as string, scp: '' }));
    setTourPlayers(selected);
  }, [playerRows]);

  const groupedGames = useMemo(() => {
    const groups = new Map<string, GameDefinition[]>();
    gameDefinitions
      .filter((game) => (game.status ?? '').toLowerCase() === 'enabled')
      .forEach((game) => {
      const key = (game.gameType ?? '').trim() || t('tournaments.gameTypeUnknown');
      const existing = groups.get(key) ?? [];
      existing.push(game);
      groups.set(key, existing);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [gameDefinitions, t]);

  const orderedRounds = useMemo(() => {
    const parseDate = (value?: string) => {
      if (!value) return Number.NaN;
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? Number.NaN : parsed;
    };
    return [...rounds].sort((a, b) => {
      const aDate = parseDate(a.playDate);
      const bDate = parseDate(b.playDate);
      if (!Number.isNaN(aDate) || !Number.isNaN(bDate)) {
        if (Number.isNaN(aDate)) return 1;
        if (Number.isNaN(bDate)) return -1;
        if (aDate !== bDate) return aDate - bDate;
      }
      const aCreated = Date.parse(a.createdAt ?? '') || 0;
      const bCreated = Date.parse(b.createdAt ?? '') || 0;
      return bCreated - aCreated;
    });
  }, [rounds]);

  const handleCreateRound = useCallback(() => {
    if (!tourId || !user?.token) return;
    if (tourPlayersLoading) {
      setCreateRoundError(t('tourEdit.roundCreatePlayerLoading'));
      return;
    }
    if (tourPlayers.length === 0) {
      setCreateRoundError(t('tourEdit.roundCreateNoPlayers'));
      return;
    }
    if (!selectedCourse) {
      setCourseError(t('course.select'));
      return;
    }
    setCreateRoundError(null);
    setCreatingRound(true);
    const requestId = generateId();
    addRoundRequestIdRef.current = requestId;
    const payload = {
      type: 'round.initiate',
      requestId,
      tourId,
      name: roundNameInput.trim() || selectedCourse.name,
      description: roundDescription.trim() || undefined,
      holesCount,
      playDate: roundPlayDateInput || getTodayDateInput(),
      course: selectedCourse,
      players: tourPlayers,
      gameDefinitionIds: [],
    };
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const sent = socket.send(payload);
    if (!sent) {
      setCreateRoundError(t('tourEdit.roundCreateFailed'));
      setCreatingRound(false);
      return;
    }
    if (addRoundAckTimeoutRef.current) {
      clearTimeout(addRoundAckTimeoutRef.current);
    }
    addRoundAckTimeoutRef.current = setTimeout(() => {
      if (addRoundRequestIdRef.current !== requestId) return;
      addRoundRequestIdRef.current = null;
      setCreatingRound(false);
      setCreateRoundError(t('tourEdit.roundCreateFailed'));
    }, 12000);
  }, [
    holesCount,
    roundDescription,
    roundNameInput,
    roundPlayDateInput,
    selectedCourse,
    t,
    tourId,
    tourPlayers,
    tourPlayersLoading,
    user?.token,
  ]);

  const updatePlayerQuery = useCallback((index: number, value: string) => {
    setPlayerRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, query: value, id: undefined, name: undefined }
          : row
      )
    );
    setPlayerRowOpen(index);
  }, []);

  const buildSelectedPlayers = useCallback(() => {
    const playerById = new Map(availablePlayers.map((player) => [player.id, player.name]));
    return playerRows
      .filter((row) => row.id)
      .map((row) => ({
        id: row.id as string,
        name: (row.name ?? playerById.get(row.id as string) ?? '').trim(),
        scp: row.locked ? row.query?.trim() : '',
      }))
      .filter((player) => player.name.length > 0);
  }, [availablePlayers, playerRows]);

  const sendTourUpdate = useCallback(
    (reason: string) => {
      if (!tourId || !user?.token) return;
      const trimmed = tourName.trim();
      if (!trimmed) {
        setError(t('tourEdit.nameRequired'));
        return;
      }
      const selectedPlayers = buildSelectedPlayers();
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.saving'));
      const requestId = generateId();
      pendingRequestIdRef.current = requestId;
      const payload = {
        type: 'tour.update',
        tourId,
        requestId,
        tourName: trimmed,
        ...(selectedPlayers.length > 0 ? { players: selectedPlayers } : {}),
      };
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingPayloadRef.current = payload;
        setStatusNote(t('tourEdit.saveQueued'));
      } else {
        setStatusNote(t('tourEdit.saveSent'));
      }
      if (ackTimeoutRef.current) {
        clearTimeout(ackTimeoutRef.current);
      }
      ackTimeoutRef.current = setTimeout(() => {
        if (pendingRequestIdRef.current !== requestId) return;
        pendingRequestIdRef.current = null;
        setSaving(false);
        setError(t('tourEdit.saveFailed'));
        logToBff('tourEdit.update.timeout', { tourId, requestId, reason }, 'warn');
      }, 12000);
    },
    [buildSelectedPlayers, logToBff, t, tourId, tourName, user?.token]
  );

  const selectPlayer = useCallback(
    (index: number, player: TourPlayerSnapshot) => {
      setPlayerRows((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index ? { ...row, id: player.id, name: player.name, query: player.name } : row
        )
      );
      setPlayerRowOpen(null);
    },
    []
  );

  const addPlayerRow = useCallback(() => {
    setPlayerRows((prev) => [...prev, { id: undefined, name: undefined, query: '', locked: false }]);
  }, []);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const trimmed = tourName.trim();
    if (!trimmed || trimmed === lastTourNameRef.current) return;
    const timeout = setTimeout(() => {
      if (tourName.trim() === lastTourNameRef.current) return;
      lastTourNameRef.current = tourName.trim();
      sendTourUpdate('tour.name');
    }, 700);
    return () => clearTimeout(timeout);
  }, [sendTourUpdate, tourId, tourName, user?.token]);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const selected = buildSelectedPlayers();
    const signature = selected.map((p) => p.id).sort().join('|');
    if (signature === lastPlayersSignatureRef.current) return;
    lastPlayersSignatureRef.current = signature;
    if (selected.length > 0 || playerRows.some((row) => row.id)) {
      sendTourUpdate('tour.players');
    }
  }, [buildSelectedPlayers, playerRows, sendTourUpdate, tourId, user?.token]);

  const pendingImageFileRef = useRef<File | null>(null);

  const handleImageSelect = useCallback(
    (event: any) => {
      const file = event.target.files?.[0] as File | undefined;
      if (!file || !tourId || !user?.token) return;
      pendingImageFileRef.current = file;
      const requestId = generateId();
      imageRequestIdRef.current = requestId;
      setImageUploading(true);
      setImageError(null);
      setStatusNote(t('tourEdit.uploadingImage'));
      const socket = socketRef.current;
      socket.setAuthToken(user?.token ?? null);
      socket.send({
        type: 'tour.image.presign',
        tourId,
        requestId,
        fileName: file.name,
        contentType: file.type || 'image/jpeg',
        fileSize: file.size,
      });
      event.target.value = '';
    },
    [t, tourId, user?.token]
  );

  return (
    <View
      style={styles.container}
      {...({
        onMouseUp: handlePointerDragEnd,
        onTouchEnd: handlePointerDragEnd,
        onMouseMove: (event: any) => {
          if (!isDragging) return;
          setDragPos({ x: event?.clientX ?? 0, y: event?.clientY ?? 0 });
        },
        onTouchMove: (event: any) => {
          if (!isDragging) return;
          const touch = event?.touches?.[0];
          if (!touch) return;
          setDragPos({ x: touch.clientX ?? 0, y: touch.clientY ?? 0 });
        },
      } as any)}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
        <View style={styles.headerText}>
          <TextInput
            value={tourName}
            onChangeText={setTourName}
            placeholder={t('tourEdit.namePlaceholder')}
            placeholderTextColor="#6f87c6"
            style={styles.titleInput}
            editable={!loading && !saving}
          />
        </View>
          <View style={styles.headerActions}>
            {tourId && tourImageKey ? (
              <Image
                source={{
                  uri: tourImageKey.startsWith('http')
                    ? `${tourImageKey}?v=${tourImageVersion}`
                    : `${apiBaseUrl}/api/tours/${tourId}/image?v=${tourImageVersion}`,
                }}
                style={styles.tourImagePreview}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.tourImagePlaceholder} />
            )}
            <Pressable
              style={[styles.addImageButton, imageUploading && styles.addImageButtonDisabled]}
              onPress={() => fileInputRef.current?.click()}
              disabled={imageUploading}
              accessibilityRole="button"
              accessibilityLabel={t('tourEdit.addImage')}
            >
              <Text style={styles.addImageButtonText}>{t('tourEdit.addImage')}</Text>
            </Pressable>
          </View>
      </View>
      {loading ? <Text style={styles.meta}>{t('tourEdit.loading')}</Text> : null}
      {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
      {imageError ? <Text style={styles.error}>{imageError}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
        <View style={styles.body}>
        <View style={styles.columnHeaderRow}>
          <Text style={[styles.columnTitle, styles.columnTitlePlayers]}>{t('tourEdit.playersTitle')}</Text>
          <View style={styles.roundsHeaderInline}>
            <Text style={[styles.columnTitle, styles.columnTitleRounds]}>{t('tournaments.roundsTitle')}</Text>
            <Pressable
              style={styles.addRoundButtonInline}
              onPress={() => {
                if (!tourId) return;
                setCreateRoundError(null);
                setCourseError(null);
                setAddRoundOpen(true);
              }}
              accessibilityRole="button"
              accessibilityLabel={t('tourEdit.addRound')}
            >
              <Text style={styles.addRoundButtonText}>{t('tourEdit.addRound')}</Text>
            </Pressable>
          </View>
          <Text style={[styles.columnTitle, styles.columnTitleGames]}>{t('tournaments.gamesTitle')}</Text>
        </View>
        <View style={styles.columnsRow}>
        <View style={styles.panelColumn}>
          <View
            style={styles.leftPanel}
            {...({
              onMouseEnter: () => {
                if (isDragging) {
                  dropTargetRef.current = null;
                }
              },
              onMouseMove: () => {
                if (isDragging) {
                  dropTargetRef.current = null;
                }
              },
            } as any)}
          >
            <View style={styles.playerPanel}>
            {playerRows.map((row, index) => {
              const query = row.query ?? '';
              const selectedIds = new Set(playerRows.map((p) => p.id).filter(Boolean) as string[]);
              const options = availablePlayers
                .filter((player) => !selectedIds.has(player.id) || player.id === row.id)
                .filter((player) => player.name.toLowerCase().includes(query.trim().toLowerCase()))
                .slice(0, 8);
              return (
                <View key={`player-row-${index}`} style={styles.playerRow}>
                  <View style={styles.playerInputWrapper}>
                    <TextInput
                      style={[styles.playerInput, row.locked && styles.playerInputLocked]}
                      value={query}
                      editable={!row.locked}
                      placeholder={t('tourEdit.playerSearchPlaceholder')}
                      onFocus={() => {
                        if (!row.locked) setPlayerRowOpen(index);
                      }}
                      onBlur={() => {
                        if (!row.locked) {
                          setTimeout(() => setPlayerRowOpen((prev) => (prev === index ? null : prev)), 120);
                        }
                      }}
                      onChangeText={(value) => {
                        if (row.locked) return;
                        updatePlayerQuery(index, value);
                      }}
                    />
                    {!row.id &&
                    query.trim().length > 0 &&
                    !availablePlayers.some((player) => player.name.trim().toLowerCase() === query.trim().toLowerCase()) ? (
                      <Text style={styles.playerNewInline} pointerEvents="none">
                        &lt;NEW&gt;
                      </Text>
                    ) : null}
                  </View>
                  {playerRowOpen === index && options.length > 0 && !row.locked ? (
                    <View style={styles.playerDropdown}>
                      {options.map((player) => (
                        <Pressable
                          key={player.id}
                          style={styles.playerOption}
                          onPress={() => selectPlayer(index, player)}
                          onMouseDown={(event: any) => {
                            event?.preventDefault?.();
                          }}
                        >
                          <Text style={styles.playerOptionText}>{player.name}</Text>
                        </Pressable>
                      ))}
                    </View>
                  ) : null}
                </View>
              );
            })}
            <Pressable style={styles.addPlayerButton} onPress={addPlayerRow}>
              <Text style={styles.addPlayerButtonText}>{t('tourEdit.addPlayer')}</Text>
            </Pressable>
            </View>
          </View>
        </View>
        <View style={styles.roundsCard}>
          {roundsLoading ? <Text style={styles.meta}>{t('tournaments.roundsLoading')}</Text> : null}
          {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
          {!roundsLoading && !roundsError && rounds.length === 0 ? (
            <Text style={styles.meta}>{t('tournaments.roundsEmpty')}</Text>
          ) : null}
          <View
            style={styles.roundsGrid}
            {...({
              onDragOver: (event: any) => {
                event?.preventDefault?.();
                if (event?.dataTransfer) {
                  event.dataTransfer.dropEffect = 'move';
                }
              },
              onMouseLeave: () => {
                if (isDragging) {
                  dropTargetRef.current = null;
                }
              },
            } as any)}
          >
            {orderedRounds.map((round) => {
              const roundName = round.name ?? round.courseName ?? t('tournaments.roundFallbackName');
              const roundGamesState = roundGamesByRoundId[round.id];
              const gameById = new Map(gameDefinitions.map((game) => [game.id, game]));
              const gameIdCount = roundGamesState?.gameIds?.length ?? 0;
              const playDateLabel = round.playDate
                ? new Date(round.playDate).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                : t('tourEdit.roundDateUnset');
              return (
                <View
                  key={round.id}
                  style={styles.roundColumn}
                  {...({
                    onMouseEnter: () => {
                      if (isDragging) {
                        dropTargetRef.current = { roundId: round.id };
                      }
                    },
                    onMouseMove: () => {
                      if (isDragging) {
                        dropTargetRef.current = { roundId: round.id };
                      }
                    },
                    onMouseLeave: () => {
                      if (dropTargetRef.current?.roundId === round.id) {
                        dropTargetRef.current = null;
                      }
                    },
                    onDragEnter: (event: any) => {
                      event?.preventDefault?.();
                      dropTargetRef.current = { roundId: round.id };
                    },
                    onDragOver: (event: any) => {
                      event?.preventDefault?.();
                      if (event?.dataTransfer) {
                        event.dataTransfer.dropEffect = 'move';
                      }
                      dropTargetRef.current = { roundId: round.id };
                    },
                    onDragLeave: () => {
                      if (dropTargetRef.current?.roundId === round.id) {
                        dropTargetRef.current = null;
                      }
                    },
                    onDrop: (event: any) => {
                      event?.preventDefault?.();
                      const draggedId = event?.dataTransfer?.getData?.('text/plain') ?? null;
                      dropTargetRef.current = { roundId: round.id };
                      handleDropToRound(round.id, draggedId);
                      setIsDragging(false);
                    },
                    onMouseUp: () => handlePointerDrop(round.id),
                    onTouchEnd: () => handlePointerDrop(round.id),
                  } as any)}
                >
                  <View
                    style={[
                      styles.roundDropZone,
                      { pointerEvents: isDragging ? 'auto' : 'none' },
                    ]}
                    {...({
                      onMouseUp: () => handlePointerDrop(round.id),
                      onTouchEnd: () => handlePointerDrop(round.id),
                      onDragOver: (event: any) => {
                        event?.preventDefault?.();
                        if (event?.dataTransfer) {
                          event.dataTransfer.dropEffect = 'move';
                        }
                        dropTargetRef.current = { roundId: round.id };
                      },
                      onDrop: (event: any) => {
                        event?.preventDefault?.();
                        const draggedId = event?.dataTransfer?.getData?.('text/plain') ?? null;
                        dropTargetRef.current = { roundId: round.id };
                        handleDropToRound(round.id, draggedId);
                        setIsDragging(false);
                      },
                    } as any)}
                  />
                  <View
                    style={styles.roundCard}
                    {...({
                      onDragOver: (event: any) => {
                        event?.preventDefault?.();
                        if (event?.dataTransfer) {
                          event.dataTransfer.dropEffect = 'move';
                        }
                        dropTargetRef.current = { roundId: round.id };
                      },
                      onDrop: (event: any) => {
                        event?.preventDefault?.();
                        const draggedId = event?.dataTransfer?.getData?.('text/plain') ?? null;
                        dropTargetRef.current = { roundId: round.id };
                        handleDropToRound(round.id, draggedId);
                        setIsDragging(false);
                      },
                    } as any)}
                  >
                    <View style={styles.roundHeader}>
                      <Text style={styles.roundName}>{roundName}</Text>
                      <Pressable
                        onPress={() => {
                          setRoundPlayDateTargetId(round.id);
                          setRoundPlayDateValue(round.playDate ? new Date(round.playDate) : new Date());
                          setPlayDatePickerOpen(true);
                        }}
                      >
                        <Text style={styles.roundPlayDate}>{playDateLabel}</Text>
                      </Pressable>
                      {debug ? <Text style={styles.roundDebugId}>{round.id}</Text> : null}
                      {round.status ? <Text style={styles.roundMeta}>{t('tournaments.roundStatus', { status: round.status })}</Text> : null}
                    </View>
                    <View style={styles.roundGamesSection}>
                      {roundGamesState?.loading ? (
                        <Text style={styles.roundGamesMeta}>{t('tournaments.gamesLoading')}</Text>
                      ) : null}
                      {roundGamesState?.error ? <Text style={styles.roundGamesError}>{roundGamesState.error}</Text> : null}
                      <View style={styles.roundGamesList}>
                        {(roundGamesState?.gameIds ?? []).map((gameId, idx) => {
                          const game = gameById.get(gameId);
                          const name = getGameDisplayName(game, t('tournaments.gameFallbackName'));
                          const description = getGameDisplayDescription(game, '');
                          const info = getGameDisplayInfo(game, '');
                          const gameType = formatGameType(game?.gameType);
                          const competitionType = game?.competitionType ?? '';
                          const calculation = game?.calculation ?? '';
                          const typeLine = [gameType, competitionType].filter(Boolean).join(' · ');
                          const weightValue =
                            roundGamesState?.weightsByGameId?.[gameId] ?? getDefaultWeightForGame(gameId);
                          const stepIndex = WEIGHT_STEPS.indexOf(weightValue);
                          const nextWeight = WEIGHT_STEPS[(stepIndex + 1) % WEIGHT_STEPS.length];
                          const isExpanded = expandedGameId === gameId;
                          return (
                            <View key={`${round.id}-${gameId}-${idx}`} style={styles.roundGameItem}>
                              <View
                                style={[styles.roundGameTag, isExpanded && styles.roundGameTagActive]}
                                {...({
                                  onMouseEnter: () => setHoveredGameKey(`${round.id}:${gameId}:${idx}`),
                                  onMouseLeave: () => setHoveredGameKey((prev) => (prev === `${round.id}:${gameId}:${idx}` ? null : prev)),
                                } as any)}
                              >
                                <Pressable
                                  style={styles.roundGameContent}
                                  onPress={() => setExpandedGameId(isExpanded ? null : gameId)}
                                  accessibilityLabel={name}
                                >
                                  <Image
                                    source={getGameImageSource(name)}
                                    style={styles.roundGameImage}
                                    resizeMode="contain"
                                    {...({
                                      draggable: false,
                                      onDragStart: (event: any) => {
                                        event?.preventDefault?.();
                                      },
                                      onDragEnd: handleDragEnd,
                                      onMouseDown: () => handlePointerDragStart(gameId, round.id),
                                      onTouchStart: () => handlePointerDragStart(gameId, round.id),
                                    } as any)}
                                  />
                                  <View style={styles.roundGameTextBlock}>
                                    <Text style={styles.roundGameText}>{name}</Text>
                                    <Text style={styles.roundGameType}>
                                      {typeLine || t('tournaments.gameTypeMissing')}
                                    </Text>
                                  </View>
                                </Pressable>
                                <Pressable
                                  style={styles.weightButton}
                                  onPress={() => {
                                    const currentWeights = roundGamesState?.weightsByGameId ?? {};
                                    updateRoundGames(round.id, roundGamesState?.gameIds ?? [], {
                                      ...currentWeights,
                                      [gameId]: nextWeight,
                                    });
                                  }}
                                  {...({
                                    onMouseEnter: () => setHoveredWeightKey(`${round.id}:${gameId}:${idx}`),
                                    onMouseLeave: () =>
                                      setHoveredWeightKey((prev) =>
                                        prev === `${round.id}:${gameId}:${idx}` ? null : prev
                                      ),
                                  } as any)}
                                >
                                  <View style={styles.weightQuad}>
                                    {WEIGHT_STEPS.map((step, index) => {
                                      const quadrantStyle =
                                        index === 0
                                          ? styles.weightQuadTopLeft
                                          : index === 1
                                            ? styles.weightQuadTopRight
                                            : index === 2
                                              ? styles.weightQuadBottomRight
                                              : styles.weightQuadBottomLeft;
                                      const activeCount = Math.max(0, Math.min(4, Math.round(weightValue / 25)));
                                      const fillRankByIndex: Record<number, number> = { 1: 0, 2: 1, 3: 2, 0: 3 };
                                      const isActive = (fillRankByIndex[index] ?? 0) < activeCount;
                                      return (
                                      <View
                                        key={step}
                                        style={[
                                          styles.weightQuadCell,
                                          quadrantStyle,
                                          isActive && styles.weightQuadActive,
                                        ]}
                                      />
                                      );
                                    })}
                                  </View>
                                </Pressable>
                                {hoveredWeightKey === `${round.id}:${gameId}:${idx}` ? (
                                  <View style={styles.weightTooltip}>
                                    <Text style={styles.weightTooltipText}>{t('tournaments.gameWeightLabel')} {weightValue}%</Text>
                                  </View>
                                ) : null}
                                {hoveredGameKey === `${round.id}:${gameId}:${idx}` && description ? (
                                  <View style={styles.hoverCard}>
                                    <Image
                                      source={getGameImageSource(name)}
                                      style={styles.hoverImage}
                                      resizeMode="contain"
                                    />
                                    <View style={styles.hoverTextWrap}>
                                      <Text style={styles.hoverText}>{description}</Text>
                                    </View>
                                  </View>
                                ) : null}
                              </View>
                              {isExpanded ? (
                                <View style={styles.roundGameDetails}>
                                  <Text style={styles.roundGameDetailsTitle}>{t('tournaments.gameDetailsTitle')}</Text>
                                  {info ? <Text style={styles.roundGameDetailsText}>{info}</Text> : null}
                                  <Text style={styles.roundGameDetailsText}>{description || t('tournaments.gameDetailsEmpty')}</Text>
                                  {calculation ? (
                                    <Text style={styles.roundGameDetailsMeta}>
                                      {t('tournaments.gameCalculationLabel')} {calculation}
                                    </Text>
                                  ) : null}
                                </View>
                              ) : null}
                            </View>
                          );
                        })}
                      </View>
                      <Pressable
                        style={styles.addGameButton}
                        onPress={() => openAddMenu(round.id)}
                        accessibilityLabel={t('tournaments.addGame')}
                      >
                        <Text style={styles.addGameButtonText}>{t('tournaments.addGame')}</Text>
                      </Pressable>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
        <View style={styles.panelColumn}>
          <View
            style={styles.leftPanel}
            {...({
              onMouseEnter: () => {
                if (isDragging) {
                  dropTargetRef.current = null;
                }
              },
              onMouseMove: () => {
                if (isDragging) {
                  dropTargetRef.current = null;
                }
              },
            } as any)}
          >
            {groupedGames.map(([group, items]) => (
              <View key={group} style={styles.gameGroup}>
                <Text style={styles.gameGroupTitle}>{group}</Text>
                {items.map((game) => (
                  <View
                    key={game.id}
                    style={styles.leftGameItem}
                    {...({
                      onMouseEnter: () => setHoveredGameKey(`left:${game.id}`),
                      onMouseLeave: () => setHoveredGameKey((prev) => (prev === `left:${game.id}` ? null : prev)),
                    } as any)}
                  >
                    <Pressable
                      style={styles.leftGameContent}
                      onPress={() => setExpandedGameId(expandedGameId === game.id ? null : game.id)}
                    >
                      <Image
                        source={getGameImageSource(game.name)}
                        style={styles.leftGameImage}
                        resizeMode="contain"
                        {...({
                          draggable: false,
                          onDragStart: (event: any) => {
                            event?.preventDefault?.();
                          },
                          onDragEnd: handleDragEnd,
                          onMouseDown: () => handlePointerDragStart(game.id, null),
                          onTouchStart: () => handlePointerDragStart(game.id, null),
                        } as any)}
                      />
                      <View style={styles.leftGameText}>
                        <Text style={styles.leftGameName}>{getGameDisplayName(game, t('tournaments.gameFallbackName'))}</Text>
                        <Text style={styles.leftGameType}>
                          {formatGameType(game.gameType) || t('tournaments.gameTypeMissing')}
                        </Text>
                      </View>
                    </Pressable>
                    {hoveredGameKey === `left:${game.id}` && getGameDisplayDescription(game, '') ? (
                      <View style={styles.hoverCard}>
                        <Image
                          source={getGameImageSource(game.name)}
                          style={styles.hoverImage}
                          resizeMode="contain"
                        />
                        <View style={styles.hoverTextWrap}>
                          <Text style={styles.hoverText}>{getGameDisplayDescription(game, '')}</Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>
        </View>
        </View>
      </ScrollView>
      {isDragging ? (
        <View
          style={[
            styles.dragPreview,
            { transform: [{ translateX: dragPos.x + 8 }, { translateY: dragPos.y + 8 }] },
          ]}
        >
          <Image
            source={getGameImageSource(getGameNameById(dragStateRef.current?.gameId, gameDefinitions))}
            style={styles.dragPreviewImage}
            resizeMode="contain"
          />
        </View>
      ) : null}
      {addMenuOpen && addRoundId ? (
        <View style={styles.addOverlay}>
          <View style={styles.addModal}>
            <View style={styles.addHeader}>
              <Text style={styles.addTitle}>{t('tournaments.addGameTitle')}</Text>
              <Pressable style={styles.addClose} onPress={closeAddMenu}>
                <Text style={styles.addCloseText}>{t('menu.close')}</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.addList} contentContainerStyle={styles.addListContent}>
              {gameDefinitions
                .filter((game) => (game.status ?? '').toLowerCase() === 'enabled')
                .filter((game) => !(roundGamesByRoundId[addRoundId]?.gameIds ?? []).includes(game.id))
                .map((game) => (
                  <Pressable
                    key={game.id}
                    style={styles.addItem}
                    onPress={() => handleAddGame(addRoundId, game.id)}
                  >
                    <Image source={getGameImageSource(game.name)} style={styles.addItemImage} resizeMode="contain" />
                    <View style={styles.addItemText}>
                      <Text style={styles.addItemName}>{getGameDisplayName(game, t('tournaments.gameFallbackName'))}</Text>
                      <Text style={styles.addItemMeta}>
                        {formatGameType(game.gameType) || t('tournaments.gameTypeMissing')}
                      </Text>
                    </View>
                  </Pressable>
                ))}
              {gameDefinitions
                .filter((game) => (game.status ?? '').toLowerCase() === 'enabled')
                .filter((game) => !(roundGamesByRoundId[addRoundId]?.gameIds ?? []).includes(game.id)).length === 0 ? (
                <Text style={styles.addEmpty}>{t('tournaments.addGameEmpty')}</Text>
              ) : null}
            </ScrollView>
          </View>
        </View>
      ) : null}
      <Modal visible={addRoundOpen} transparent animationType="fade" onRequestClose={() => setAddRoundOpen(false)}>
        <Pressable style={styles.addRoundBackdrop} onPress={() => setAddRoundOpen(false)}>
          <Pressable style={styles.addRoundCard} onPress={() => {}}>
            <Text style={styles.addRoundTitle}>{t('tourEdit.addRound')}</Text>
            <View style={styles.addRoundCourseCard}>
              <View style={styles.addRoundCourseHeader}>
                <Text style={styles.addRoundCourseLabel}>{t('course.label')}</Text>
                <Pressable
                  style={[styles.addRoundRefreshButton, loadingCourses && styles.addRoundRefreshButtonDisabled]}
                  onPress={() => fetchCourses()}
                  disabled={loadingCourses}
                >
                  <Text style={styles.addRoundRefreshText}>{t('course.refresh')}</Text>
                </Pressable>
              </View>
              {locationDenied ? <Text style={styles.addRoundError}>{t('course.permissionDenied')}</Text> : null}
              {courseError ? <Text style={styles.addRoundError}>{courseError}</Text> : null}
              {loadingCourses ? <ActivityIndicator /> : null}
              <ScrollView style={styles.addRoundCourseList}>
                {courses.map((course) => (
                  <Pressable
                    key={course.id}
                    style={[
                      styles.addRoundCourseOption,
                      course.id === selectedCourseId && styles.addRoundCourseOptionActive,
                    ]}
                    onPress={() => setSelectedCourseId(course.id)}
                  >
                    <Text style={styles.addRoundCourseOptionTitle}>{course.name}</Text>
                    <Text style={styles.addRoundCourseOptionSubtitle}>{course.address}</Text>
                  </Pressable>
                ))}
              </ScrollView>
              <View style={styles.addRoundMetaRow}>
                <Pressable
                  style={styles.addRoundDescriptionButton}
                  onPress={() => setRoundDescriptionOpen(true)}
                  accessibilityLabel={t('players.roundDescriptionButton')}
                >
                  <Text style={styles.addRoundDescriptionButtonText}>⋯</Text>
                </Pressable>
                <View style={styles.addRoundNameBlock}>
                  <Text style={styles.addRoundNameLabel}>{t('tourEdit.roundNameLabel')}</Text>
                  <TextInput
                    value={roundNameInput}
                    onChangeText={setRoundNameInput}
                    placeholder={t('players.roundNamePlaceholder')}
                    placeholderTextColor="#7f8ca3"
                    style={styles.addRoundNameInput}
                  />
                </View>
              </View>
              <View style={styles.addRoundDateRow}>
                <Text style={styles.addRoundDateLabel}>{t('tourEdit.roundDateLabel')}</Text>
                <Pressable
                  style={styles.addRoundDateButton}
                  onPress={() => {
                    setRoundPlayDateTargetId(null);
                    setRoundPlayDateValue(null);
                    setPlayDatePickerOpen(true);
                  }}
                >
                  <Text style={styles.addRoundDateButtonText}>{roundPlayDateInput}</Text>
                </Pressable>
              </View>
              <View style={styles.addRoundHolesRow}>
                <Text style={styles.addRoundHolesLabel}>{t('players.holesCountLabel')}</Text>
                <View style={styles.addRoundHolesOptions}>
                  {[9, 18].map((count) => (
                    <Pressable
                      key={count}
                      style={[
                        styles.addRoundHolesOption,
                        holesCount === count && styles.addRoundHolesOptionActive,
                      ]}
                      onPress={() => setHolesCount(count)}
                      accessibilityLabel={t('players.holesCountOptionLabel', { count })}
                    >
                      <Text
                        style={[
                          styles.addRoundHolesText,
                          holesCount === count && styles.addRoundHolesTextActive,
                        ]}
                      >
                        {t('players.holesCountOptionValue', { count })}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            </View>
            {createRoundError ? <Text style={styles.addRoundError}>{createRoundError}</Text> : null}
            <View style={styles.addRoundActions}>
              <Pressable style={styles.addRoundCancel} onPress={() => setAddRoundOpen(false)}>
                <Text style={styles.addRoundCancelText}>{t('tourEdit.cancelRound')}</Text>
              </Pressable>
              <Pressable
                style={[styles.addRoundConfirm, creatingRound && styles.addRoundConfirmDisabled]}
                onPress={handleCreateRound}
                disabled={creatingRound}
              >
                <Text style={styles.addRoundConfirmText}>
                  {creatingRound ? t('tourEdit.roundCreateSaving') : t('tourEdit.roundCreate')}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={roundDescriptionOpen} transparent animationType="fade" onRequestClose={() => setRoundDescriptionOpen(false)}>
        <Pressable style={styles.addRoundBackdrop} onPress={() => setRoundDescriptionOpen(false)}>
          <Pressable style={styles.addRoundDescCard} onPress={() => {}}>
            <Text style={styles.addRoundTitle}>{t('players.roundDescriptionTitle')}</Text>
            <TextInput
              value={roundDescription}
              onChangeText={setRoundDescription}
              placeholder={t('players.roundDescriptionPlaceholder')}
              placeholderTextColor="#7f8ca3"
              multiline
              style={styles.addRoundDescInput}
            />
            <Pressable style={styles.addRoundConfirm} onPress={() => setRoundDescriptionOpen(false)}>
              <Text style={styles.addRoundConfirmText}>{t('tourEdit.done')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={playDatePickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setPlayDatePickerOpen(false);
          setRoundPlayDateTargetId(null);
        }}
      >
        <Pressable
          style={styles.addRoundBackdrop}
          onPress={() => {
            setPlayDatePickerOpen(false);
            setRoundPlayDateTargetId(null);
          }}
        >
          <Pressable style={styles.datePickerModalCard} onPress={() => {}}>
            <Text style={styles.addRoundTitle}>{t('tourEdit.roundDateLabel')}</Text>
            <View style={styles.addRoundDatePickerInline}>
              <DatePicker
                selected={roundPlayDateTargetId ? selectedRoundPlayDate : selectedPlayDate}
                onChange={(date: Date | null) => {
                  if (!date) return;
                  if (roundPlayDateTargetId) {
                    setRoundPlayDateValue(date);
                    setPlayDatePickerOpen(false);
                    updateRoundPlayDate(roundPlayDateTargetId, date);
                    setRoundPlayDateTargetId(null);
                    return;
                  }
                  setRoundPlayDateInput(formatDateInput(date));
                  setPlayDatePickerOpen(false);
                }}
                dateFormat="yyyy-MM-dd"
                inline
                className="goffy-date-picker"
                wrapperClassName="goffy-date-picker-wrapper"
              />
            </View>
            <Pressable style={styles.addRoundConfirm} onPress={() => setPlayDatePickerOpen(false)}>
              <Text style={styles.addRoundConfirmText}>{t('tourEdit.done')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      {typeof document !== 'undefined' ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a1228',
  },
  scrollContent: {
    padding: 28,
    gap: 24,
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  headerText: {
    flex: 1,
    gap: 6,
  },
  headerActions: {
    alignItems: 'flex-end',
    gap: 8,
  },
  tourImagePreview: {
    width: 84,
    height: 84,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0b1224',
  },
  tourImagePlaceholder: {
    width: 84,
    height: 84,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0b1224',
  },
  addImageButton: {
    backgroundColor: '#1f2d4f',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  addImageButtonDisabled: {
    opacity: 0.6,
  },
  addImageButtonText: {
    color: '#e5edff',
    fontSize: 12,
    fontWeight: '700',
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
    color: '#f8fafc',
  },
  titleInput: {
    fontSize: 32,
    fontWeight: '800',
    color: '#f8fafc',
    paddingVertical: 2,
    paddingHorizontal: 0,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(125,178,255,0.35)',
  },
  meta: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  error: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '700',
  },
  body: {
    flexDirection: 'column',
    gap: 20,
    alignItems: 'stretch',
    flex: 1,
  },
  columnHeaderRow: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-end',
    width: '100%',
  },
  roundsHeaderInline: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  columnsRow: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
    width: '100%',
  },
  panelColumn: {
    gap: 8,
  },
  columnTitle: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  columnTitlePlayers: {
    width: 260,
  },
  columnTitleRounds: {
    flex: 0,
  },
  columnTitleGames: {
    width: 260,
  },
  leftPanel: {
    width: 260,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#f59e0b',
    backgroundColor: '#20120a',
    padding: 16,
    gap: 12,
    position: 'relative',
    zIndex: 80,
  },
  leftPanelTitle: {
    color: '#fcd34d',
    fontSize: 14,
    fontWeight: '800',
  },
  playerPanel: {
    gap: 10,
    zIndex: 60,
  },
  playerRow: {
    position: 'relative',
    zIndex: 60,
  },
  playerInputWrapper: {
    position: 'relative',
  },
  playerInput: {
    borderWidth: 1,
    borderColor: '#92400e',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    paddingRight: 60,
    color: '#f8fafc',
    backgroundColor: '#29170b',
    fontSize: 13,
  },
  playerInputLocked: {
    opacity: 0.8,
  },
  playerDropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: '#1a120d',
    borderWidth: 1,
    borderColor: '#92400e',
    borderRadius: 10,
    zIndex: 500,
    elevation: 10,
    paddingVertical: 6,
  },
  playerNewInline: {
    position: 'absolute',
    right: 10,
    top: 9,
    color: '#f59e0b',
    fontSize: 11,
    fontWeight: '700',
  },
  playerOption: {
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  playerOptionText: {
    color: '#f8fafc',
    fontSize: 13,
  },
  addPlayerButton: {
    marginTop: 4,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f59e0b',
    backgroundColor: '#2c1a0e',
    alignItems: 'center',
  },
  addPlayerButtonText: {
    color: '#fcd34d',
    fontSize: 12,
    fontWeight: '700',
  },
  leftPanelDivider: {
    height: 1,
    backgroundColor: 'rgba(245, 158, 11, 0.4)',
    marginVertical: 4,
  },
  gameGroup: {
    gap: 8,
  },
  gameGroupTitle: {
    color: '#fbbf24',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  leftGameItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#92400e',
    backgroundColor: '#29170b',
    position: 'relative',
  },
  leftGameImage: {
    width: 26,
    height: 26,
  },
  leftGameText: {
    flex: 1,
    gap: 2,
  },
  leftGameName: {
    color: '#fef3c7',
    fontSize: 12,
    fontWeight: '700',
  },
  leftGameType: {
    color: '#fcd34d',
    fontSize: 11,
  },
  leftGameContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  roundsCard: {
    flex: 1,
    gap: 12,
  },
  roundsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  roundsTitle: {
    color: '#dbeafe',
    fontSize: 14,
    fontWeight: '800',
  },
  addRoundButton: {
    backgroundColor: '#1d4ed8',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
  },
  addRoundButtonInline: {
    backgroundColor: '#1d4ed8',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  addRoundButtonText: {
    color: '#e5edff',
    fontWeight: '700',
    fontSize: 12,
  },
  addRoundBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(4,12,28,0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  addRoundCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#0b1224',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    padding: 16,
    gap: 12,
  },
  addRoundTitle: {
    color: '#e5edff',
    fontSize: 18,
    fontWeight: '700',
  },
  addRoundCourseCard: {
    backgroundColor: '#0f1c3c',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#24346b',
    padding: 12,
    gap: 10,
  },
  addRoundCourseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addRoundCourseLabel: {
    color: '#dbeafe',
    fontSize: 13,
    fontWeight: '700',
  },
  addRoundRefreshButton: {
    backgroundColor: '#1f2d4f',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  addRoundRefreshButtonDisabled: {
    opacity: 0.6,
  },
  addRoundRefreshText: {
    color: '#e5edff',
    fontWeight: '600',
    fontSize: 12,
  },
  addRoundCourseList: {
    maxHeight: 140,
  },
  addRoundCourseOption: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0b1224',
    gap: 2,
    marginBottom: 8,
  },
  addRoundCourseOptionActive: {
    borderColor: '#60a5fa',
    backgroundColor: '#101f45',
  },
  addRoundCourseOptionTitle: {
    color: '#e2e8f0',
    fontWeight: '700',
    fontSize: 13,
  },
  addRoundCourseOptionSubtitle: {
    color: '#9fb3ff',
    fontSize: 11,
  },
  addRoundMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  addRoundDescriptionButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#0b1224',
    borderWidth: 1,
    borderColor: '#1f2d4f',
  },
  addRoundDescriptionButtonText: {
    color: '#dbeafe',
    fontSize: 16,
    fontWeight: '700',
  },
  addRoundNameBlock: {
    flex: 1,
    gap: 6,
  },
  addRoundNameLabel: {
    color: '#cbd5f5',
    fontSize: 12,
    fontWeight: '600',
  },
  addRoundNameInput: {
    backgroundColor: '#0b1224',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: '#e5edff',
  },
  addRoundDateRow: {
    gap: 6,
    marginTop: 12,
  },
  addRoundDateLabel: {
    color: '#cbd5f5',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  addRoundDateButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#0b1224',
  },
  addRoundDateButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  addRoundDatePickerInline: {
    backgroundColor: '#0b1224',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  datePickerModalCard: {
    backgroundColor: '#0f1c3c',
    borderRadius: 18,
    padding: 18,
    width: 320,
    gap: 12,
    borderWidth: 1,
    borderColor: '#223a70',
  },
  addRoundHolesRow: {
    gap: 8,
  },
  addRoundHolesLabel: {
    color: '#cbd5f5',
    fontSize: 12,
    fontWeight: '700',
  },
  addRoundHolesOptions: {
    flexDirection: 'row',
    gap: 8,
  },
  addRoundHolesOption: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    alignItems: 'center',
    backgroundColor: '#0b1224',
  },
  addRoundHolesOptionActive: {
    borderColor: '#60a5fa',
    backgroundColor: '#101f45',
  },
  addRoundHolesText: {
    color: '#e2e8f0',
    fontSize: 12,
    fontWeight: '600',
  },
  addRoundHolesTextActive: {
    color: '#dbeafe',
  },
  addRoundActions: {
    flexDirection: 'row',
    gap: 10,
  },
  addRoundCancel: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    alignItems: 'center',
    backgroundColor: '#0b1224',
  },
  addRoundCancelText: {
    color: '#cbd5f5',
    fontWeight: '600',
  },
  addRoundConfirm: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: '#2563eb',
  },
  addRoundConfirmDisabled: {
    opacity: 0.6,
  },
  addRoundConfirmText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  addRoundError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  addRoundDescCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#0b1224',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    padding: 16,
    gap: 12,
  },
  addRoundDescInput: {
    backgroundColor: '#0b1224',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    padding: 10,
    minHeight: 90,
    color: '#e5edff',
  },
  roundsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  roundColumn: {
    minWidth: 220,
    maxWidth: 260,
    flexGrow: 1,
    alignSelf: 'flex-start',
    position: 'relative',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    padding: 12,
  },
  roundCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0d1733',
    gap: 8,
  },
  roundHeader: {
    gap: 2,
  },
  roundName: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '700',
  },
  roundPlayDate: {
    color: '#a7c0f6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundDebugId: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.4,
  },
  roundMeta: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  roundGamesSection: {
    gap: 6,
  },
  roundGamesTitle: {
    color: '#c7d2fe',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  roundGamesMeta: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  roundGamesError: {
    color: '#ff9fa6',
    fontSize: 12,
    fontWeight: '600',
  },
  roundGamesList: {
    flexDirection: 'column',
    gap: 6,
  },
  addGameButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#34d399',
    backgroundColor: 'rgba(52,211,153,0.12)',
  },
  addGameButtonText: {
    color: '#34d399',
    fontSize: 12,
    fontWeight: '700',
  },
  roundGameItem: {
    gap: 6,
  },
  dragPreview: {
    position: 'fixed',
    left: 0,
    top: 0,
    width: 36,
    height: 36,
    pointerEvents: 'none',
    zIndex: 50,
  },
  dragPreviewImage: {
    width: 36,
    height: 36,
  },
  addOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(5,9,20,0.6)',
    zIndex: 60,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  addModal: {
    width: 420,
    maxWidth: '90%',
    maxHeight: '80%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    padding: 16,
    gap: 12,
  },
  addHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  addTitle: {
    color: '#f8fafc',
    fontSize: 16,
    fontWeight: '800',
  },
  addClose: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  addCloseText: {
    color: '#9fb3ff',
    fontSize: 12,
    fontWeight: '700',
  },
  addList: {
    maxHeight: 360,
  },
  addListContent: {
    gap: 10,
    paddingBottom: 12,
  },
  addItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0d1733',
  },
  addItemImage: {
    width: 32,
    height: 32,
  },
  addItemText: {
    flex: 1,
    gap: 2,
  },
  addItemName: {
    color: '#e2e8f0',
    fontSize: 13,
    fontWeight: '700',
  },
  addItemMeta: {
    color: '#9fb3ff',
    fontSize: 11,
  },
  addEmpty: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  roundGameTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: 'rgba(125,178,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(125,178,255,0.35)',
    position: 'relative',
  },
  roundGameImage: {
    width: 28,
    height: 28,
  },
  roundGameContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  roundGameTextBlock: {
    flex: 1,
  },
  roundGameTagActive: {
    borderColor: 'rgba(125,178,255,0.7)',
    backgroundColor: 'rgba(125,178,255,0.22)',
  },
  roundGameText: {
    color: '#dbeafe',
    fontSize: 12,
    fontWeight: '700',
  },
  roundGameType: {
    color: '#9fb3ff',
    fontSize: 11,
    marginTop: 2,
  },
  weightButton: {
    alignItems: 'flex-end',
    gap: 4,
    paddingLeft: 8,
  },
  weightQuad: {
    width: 22,
    height: 22,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#3b4a7a',
    overflow: 'hidden',
    position: 'relative',
  },
  weightQuadCell: {
    position: 'absolute',
    width: 11,
    height: 11,
    backgroundColor: 'rgba(59,74,122,0.25)',
  },
  weightQuadActive: {
    backgroundColor: '#f59e0b',
  },
  weightQuadTopLeft: {
    top: 0,
    left: 0,
  },
  weightQuadTopRight: {
    top: 0,
    right: 0,
  },
  weightQuadBottomLeft: {
    bottom: 0,
    left: 0,
  },
  weightQuadBottomRight: {
    bottom: 0,
    right: 0,
  },
  weightTooltip: {
    position: 'absolute',
    right: 6,
    top: -28,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fbbf24',
    backgroundColor: '#0b1224',
    zIndex: 40,
  },
  weightTooltipText: {
    color: '#fcd34d',
    fontSize: 10,
    fontWeight: '700',
  },
  roundGameDetails: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: '#0b1733',
    borderWidth: 1,
    borderColor: '#22335f',
  },
  roundGameDetailsTitle: {
    color: '#c7d2fe',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  roundGameDetailsText: {
    color: '#e2e8f0',
    fontSize: 12,
  },
  roundGameDetailsMeta: {
    color: '#9fb3ff',
    fontSize: 11,
    marginTop: 6,
  },
  hoverCard: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    marginBottom: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#60a5fa',
    backgroundColor: '#0b1224',
    shadowColor: '#0ea5e9',
    shadowOpacity: 0.8,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    maxWidth: 350,
    minWidth: 250,
    zIndex: 20,
    gap: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  hoverImage: {
    width: 100,
    height: 100,
    alignSelf: 'flex-start',
  },
  hoverTextWrap: {
    flex: 1,
  },
  hoverText: {
    color: '#e2e8f0',
    fontSize: 12,
    lineHeight: 16,
  },
  roundDropZone: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 5,
  },
});

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getGameImageSource(name?: string | null) {
  const slug = getGameSlug(name);
  if (!slug) return MysterySelectImage;
  return GAME_IMAGE_MAP[slug] ?? MysterySelectImage;
}

function getGameSlug(name?: string | null) {
  const fromName = (name ?? '').trim();
  if (!fromName) return '';
  return fromName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getGameNameById(gameId: string | null | undefined, games: GameDefinition[]) {
  if (!gameId) return null;
  const match = games.find((g) => g.id === gameId);
  return match?.name ?? null;
}

let transparentDragImage: HTMLImageElement | null = null;

function getTransparentDragImage(): HTMLImageElement | null {
  if (typeof document === 'undefined') return null;
  if (transparentDragImage) return transparentDragImage;
  const img = new Image();
  img.src =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  transparentDragImage = img;
  return img;
}
